import { useEffect } from 'react';
import { SITE_NAME, LOCALE, TWITTER_CARD, absoluteUrl } from './siteConfig';

const JSON_LD_ID = 'page-json-ld';

function ogImageMimeType(imagePath) {
  if (!imagePath) return 'image/png';
  if (imagePath.endsWith('.webp')) return 'image/webp';
  if (imagePath.endsWith('.jpg') || imagePath.endsWith('.jpeg')) return 'image/jpeg';
  return 'image/png';
}

function upsertMeta(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function setMetaName(name, content) {
  if (!content) return;
  const el = upsertMeta(`meta[name="${name}"]`, () => {
    const node = document.createElement('meta');
    node.setAttribute('name', name);
    return node;
  });
  el.setAttribute('content', content);
}

function setMetaProperty(property, content) {
  if (!content) return;
  const el = upsertMeta(`meta[property="${property}"]`, () => {
    const node = document.createElement('meta');
    node.setAttribute('property', property);
    return node;
  });
  el.setAttribute('content', content);
}

function setCanonical(href) {
  if (!href) return;
  const el = upsertMeta('link[rel="canonical"]', () => {
    const node = document.createElement('link');
    node.setAttribute('rel', 'canonical');
    return node;
  });
  el.setAttribute('href', href);
}

function setRobots(noindex) {
  const content = noindex ? 'noindex, nofollow' : 'index, follow';
  setMetaName('robots', content);
}

function setJsonLd(blocks) {
  const existing = document.getElementById(JSON_LD_ID);
  if (existing) existing.remove();

  if (!blocks?.length) return;

  const script = document.createElement('script');
  script.id = JSON_LD_ID;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(blocks.length === 1 ? blocks[0] : blocks);
  document.head.appendChild(script);
}

export default function usePageSeo(seo) {
  useEffect(() => {
    if (!seo) return undefined;

    const imageUrl = absoluteUrl(seo.image);
    const imageType = seo.imageType || ogImageMimeType(seo.image);

    document.title = seo.title;
    setMetaName('description', seo.description);
    setRobots(Boolean(seo.noindex));
    setCanonical(seo.canonical);

    setMetaProperty('og:locale', LOCALE);
    setMetaProperty('og:site_name', SITE_NAME);
    setMetaProperty('og:type', seo.ogType || 'website');
    setMetaProperty('og:title', seo.title);
    setMetaProperty('og:description', seo.description);
    setMetaProperty('og:url', seo.canonical);
    setMetaProperty('og:image', imageUrl);
    setMetaProperty('og:image:secure_url', imageUrl);
    setMetaProperty('og:image:type', imageType);
    setMetaProperty('og:image:alt', seo.title);

    setMetaName('twitter:card', TWITTER_CARD);
    setMetaName('twitter:title', seo.title);
    setMetaName('twitter:description', seo.description);
    setMetaName('twitter:image', imageUrl);
    setMetaName('twitter:image:alt', seo.title);

    setJsonLd(seo.jsonLd);

    return () => {
      const node = document.getElementById(JSON_LD_ID);
      if (node) node.remove();
    };
  }, [seo]);
}
